import React, { useState } from 'react';
import { X, MessageCircle, ExternalLink, Calendar, User, Mail, Phone as PhoneIcon, Users as UsersIcon } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { CONFIG } from '../data/villaData';

export default function BookingModal({ open, onClose }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', checkin: '', checkout: '', guests: '2', message: ''
  });

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const lines = [
      '*Moonlit Villa - Booking Request*',
      '',
      `*Name:* ${form.name}`,
      `*Email:* ${form.email}`,
      `*Phone:* ${form.phone}`,
      `*Check-in:* ${form.checkin}`,
      `*Check-out:* ${form.checkout}`,
      `*Guests:* ${form.guests}`,
      form.message ? `*Message:* ${form.message}` : ''
    ].filter(Boolean).join('\n');
    const url = `https://wa.me/${CONFIG.whatsapp}?text=${encodeURIComponent(lines)}`;
    window.open(url, '_blank');
  };

  const handleChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-lg w-full my-8 shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors z-10">
          <X size={18} />
        </button>

        <div className="p-8 md:p-10">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-full bg-[#25D366]/10 flex items-center justify-center mx-auto mb-4">
              <MessageCircle size={26} className="text-[#25D366]" />
            </div>
            <h3 className="font-serif text-3xl text-stone-900 mb-2">{t.booking.title}</h3>
            <p className="text-stone-600 text-sm">{t.booking.subtitle}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <Field icon={User} placeholder={t.booking.name} value={form.name} onChange={handleChange('name')} required />
            <div className="grid grid-cols-2 gap-3">
              <Field icon={Mail} type="email" placeholder={t.booking.email} value={form.email} onChange={handleChange('email')} required />
              <Field icon={PhoneIcon} type="tel" placeholder={t.booking.phone} value={form.phone} onChange={handleChange('phone')} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field icon={Calendar} type="date" label={t.booking.checkin} value={form.checkin} onChange={handleChange('checkin')} required />
              <Field icon={Calendar} type="date" label={t.booking.checkout} value={form.checkout} onChange={handleChange('checkout')} required />
            </div>
            <Field icon={UsersIcon} type="number" placeholder={t.booking.guests} value={form.guests} onChange={handleChange('guests')} min="1" max="20" required />
            <textarea
              placeholder={t.booking.message}
              value={form.message}
              onChange={handleChange('message')}
              rows={3}
              className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:border-[#b8945e] focus:bg-white transition-colors text-stone-800 text-sm resize-none"
            />

            <button type="submit" className="w-full py-4 bg-[#25D366] hover:bg-[#1ebe5b] text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors shadow-lg">
              <MessageCircle size={20} />
              {t.booking.send}
            </button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-stone-200" /></div>
              <div className="relative flex justify-center"><span className="px-3 bg-white text-xs text-stone-500">— or —</span></div>
            </div>

            <a href={CONFIG.bookingUrl} target="_blank" rel="noopener noreferrer" className="w-full py-3.5 bg-[#003580] hover:bg-[#002650] text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors">
              {t.booking.external}
              <ExternalLink size={16} />
            </a>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, ...props }) {
  return (
    <div>
      {label && <label className="block text-xs text-stone-500 mb-1 ml-1">{label}</label>}
      <div className="relative">
        <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <input
          {...props}
          className="w-full pl-10 pr-4 py-3 bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:border-[#b8945e] focus:bg-white transition-colors text-stone-800 text-sm"
        />
      </div>
    </div>
  );
}
